/**
 * Date utility functions for the UsersDemo component
 */

/**
 * Formats a date string for table display in "Jan 15, 2024" format
 * @param dateString - ISO date string or date-like string
 * @returns Formatted date string or fallback value
 */
export const formatDateForTable = (dateString: string): string => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  // Check if the date is valid before formatting
  if (isNaN(date.getTime())) {
    return dateString; // Return original string if invalid date
  }
  
  const options: Intl.DateTimeFormatOptions = { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  };
  
  return date.toLocaleDateString('en-US', options);
};

/**
 * Formats a date range for display (e.g., "Jan 20-22, 2024")
 * @param startDate - Start date string
 * @param endDate - End date string
 * @returns Formatted date range string
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return 'N/A';
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return `${startDate} - ${endDate}`;
  }
  
  const startMonth = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endMonth = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  // If same month, show "Jan 20-22, 2024"
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${startMonth}-${end.getDate()}, ${end.getFullYear()}`;
  }
  
  // If different months, show "Jan 20 - Feb 15, 2024"
  return `${startMonth} - ${endMonth}`;
};

/**
 * Checks if a date is expired
 * @param dateString - Date string to check
 * @returns True if date is expired
 */
export const isDateExpired = (dateString: string): boolean => {
  if (!dateString || dateString === 'N/A') return false;
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return false;
  
  return date < new Date();
};

/**
 * Gets relative time description (e.g., "2 days ago", "next week")
 * @param dateString - Date string to format
 * @returns Relative time string
 */
export const getRelativeTime = (dateString: string): string => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 0) return `In ${diffDays} days`;
  if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
  
  return formatDateForTable(dateString);
};

/**
 * A calendar day (`YYYY-MM-DD`) with the month written out — "Oct 1, 2026". The admin never shows a
 * day as dd/mm or mm/dd: "01/10" reads as October 1st or January 10th depending on who looks, and
 * a closure saved on the wrong day closes the business on the wrong day. Parsed as a local date
 * (no UTC shift). '' for an empty or malformed value.
 */
export const formatDay = (isoDay: string | null | undefined, locale = 'en-US'): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay ?? '');
  if (!m) return '';
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (date.getMonth() !== Number(m[2]) - 1) return '';
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
};

/** A local date-time (`YYYY-MM-DDTHH:mm`) with the month written out — "Oct 1, 2026, 7:00 PM". '' if malformed. */
export const formatDayTime = (local: string | null | undefined, locale = 'en-US'): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local ?? '');
  if (!m) return '';
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (date.getMonth() !== Number(m[2]) - 1) return '';
  return date.toLocaleString(locale, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** `YYYY-MM-DD` for the native date input; the scanner may send other shapes. */
export const toIsoDate = (raw: string | null | undefined): string => {
  const v = (raw || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

/** Month written out (UI rule), read as a calendar day so no time zone moves it. */
export const writtenDate = (iso: string): string =>
  iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '';

/** A scanned date of birth with the month written out ("Nov 19, 1995"); the raw text if the scanner sent something unreadable. */
export const writtenDob = (raw: string | null | undefined): string => {
  const iso = toIsoDate(raw);
  return iso ? writtenDate(iso) : (raw || '').trim();
};
